import React, { useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { Select, Spin } from "antd";
import type { SelectProps } from "antd/es/select";
import { ReportsSearchValues } from "../ReportsTypes";

export interface DebounceSelectProps<ValueType = ReportsSearchValues[]>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  handleSearch: (value: string) => ValueType[];
  debounceTimeout?: number;
}

export const DebounceSelect = <
  ValueType extends { key?: string; label: React.ReactNode; value: string | number } = any
>({
  handleSearch,
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<ValueType>) => {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      const newOptions = handleSearch(value);
      if (fetchId !== fetchRef.current) {
        // for fetch callback order
        return;
      }

      setOptions(newOptions);
      setFetching(false);
    };

    return debounce(loadOptions, debounceTimeout);
  }, [handleSearch, debounceTimeout]);

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
};
